// src/components/ExperienceForm.js
import React, { useState } from "react";
import {
  Box,
  TextField,
  InputLabel,
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  CircularProgress,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { green } from "@mui/material/colors";
import Icon from "@mui/material/Icon";

const ExperienceForm = ({
  experienceData,
  setExperienceData,
  experienceFile,
  setExperienceFile,
  imageProgress,
  imageProgressValue,
  onAddExperience,
  loading,
  error,
  experienceUid, // Add experienceUid prop
  destinationFolder, // Add destinationFolder prop
}) => {
  const [fieldErrors, setFieldErrors] = useState({});

  // Validation function
  const validateFields = () => {
    const errors = {};
    if (!experienceData.id) {
      errors.id = "Experience ID is required";
      console.log("Experience ID is required");
    }
    if (!experienceData.description) {
      errors.description = "Description is required";
      console.log("Description is required");
    }
    if (!experienceData.version) {
      errors.version = "Version is required";
      console.log("Version is required");
    }
    if (!experienceData.contentBucket) {
      errors.contentBucket = "Content Bucket URL is required";
      console.log("Content Bucket URL is required");
    } else if (!/^(https?|gs|www|http):\/\/.+/.test(experienceData.contentBucket)) {
      errors.contentBucket = "Content Bucket URL is invalid";
      console.log("Content Bucket URL is invalid");
    }
    if (Object.keys(experienceData.titles).length === 0) {
      errors.titles = "At least one title is required";
      console.log("At least one title is required");
    }
    if (Object.keys(experienceData.beacons).length === 0) {
      errors.beacons = "At least one beacon is required";
      console.log("At least one beacon is required");
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    console.log("Error == ", error);
    e.preventDefault();
    if (!validateFields()) {
      console.log("Error == ", error);
    }
    if (validateFields()) {
      onAddExperience(e);
    }
  };

  return (
    <Box
      component="form"
      sx={{
        "& .MuiTextField-root": {
          m: 2,
          maxWidth: "100%",
          display: "flex",
          direction: "column",
          justifyContent: "center",
        },
      }}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <TextField
        label="Experience ID"
        type="text"
        required
        value={experienceData.id}
        onChange={(e) =>
          setExperienceData({
            ...experienceData,
            id: e.target.value,
          })
        }
        error={!!fieldErrors.id}
        helperText={fieldErrors.id}
      />
      <Box>
        <InputLabel>Titles</InputLabel>
        {Object.entries(experienceData.titles).map(([lang, title], index) => (
          <Box key={index} display="flex" alignItems="center" mb={1}>
            <FormControl sx={{ width: "30%", mr: 2 }}>
              <Select
                value={lang}
                onChange={(e) => {
                  const updatedTitles = { ...experienceData.titles };
                  const newLang = e.target.value;
                  updatedTitles[newLang] = updatedTitles[lang];
                  delete updatedTitles[lang];
                  setExperienceData({ ...experienceData, titles: updatedTitles });
                }}
              >
                <MenuItem value="en_CA">English</MenuItem>
                <MenuItem value="fr_CA">French</MenuItem>
                <MenuItem value="es_LATAM">Spanish</MenuItem>
                <MenuItem value={lang}>{lang}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              sx={{ flexGrow: 1 }}
              placeholder="Title"
              value={title}
              onChange={(e) => {
                setExperienceData({
                  ...experienceData,
                  titles: { ...experienceData.titles, [lang]: e.target.value },
                });
              }}
            />
            <IconButton
              onClick={() => {
                const updatedTitles = { ...experienceData.titles };
                delete updatedTitles[lang];
                setExperienceData({ ...experienceData, titles: updatedTitles });
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => {
            setExperienceData({
              ...experienceData,
              titles: { ...experienceData.titles, "": "" },
            });
          }}
        >
          Add Title
        </Button>
        {fieldErrors.titles && (
          <Box color="red" mt={1}>
            {fieldErrors.titles}
          </Box>
        )}
      </Box>

      <TextField
        label="Description"
        type="text"
        multiline
        rows={4}
        required
        value={experienceData.description}
        onChange={(e) =>
          setExperienceData({
            ...experienceData,
            description: e.target.value,
          })
        }
        error={!!fieldErrors.description}
        helperText={fieldErrors.description}
      />
      <TextField
        label="Version"
        type="text"
        required
        value={experienceData.version}
        onChange={(e) =>
          setExperienceData({
            ...experienceData,
            version: e.target.value,
          })
        }
        error={!!fieldErrors.version}
        helperText={fieldErrors.version}
      />

      <TextField
        label="Content Bucket URL"
        type="url"
        required
        value={experienceData.contentBucket}
        onChange={(e) =>
          setExperienceData({
            ...experienceData,
            contentBucket: e.target.value,
          })
        }
        error={!!fieldErrors.contentBucket}
        helperText={fieldErrors.contentBucket}
      />
      <Box sx={{ maxWidth: "100%", m: 2 }}>
        <FormControl fullWidth>
          <InputLabel htmlFor="outlined-adornment-file">Experience Image</InputLabel>
          <OutlinedInput
            sx={{ height: "2.8rem" }}
            id="outlined-adornment-file"
            startAdornment={
              <>
                <InputAdornment position="start">
                  <input
                    multiple
                    type="file"
                    onChange={(e) => setExperienceFile(e.target.files[0])}
                  />
                </InputAdornment>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    size={25}
                    sx={{
                      color: green[500],
                    }}
                    value={imageProgress}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {imageProgressValue === 100 ? <CheckIcon /> : null}
                  </Box>
                </Box>
              </>
            }
            label="Experience Image"
          />
        </FormControl>
      </Box>
      <Box>
        <InputLabel>Beacons</InputLabel>
        {Object.entries(experienceData.beacons).map(([activationId, mac], index) => (
          <Box key={index} display="flex" alignItems="center" mb={1}>
            <TextField
              sx={{ width: "40%", mr: 2 }}
              placeholder="Activation ID"
              value={activationId}
              onChange={(e) => {
                const updatedBeacons = { ...experienceData.beacons };
                const newActivationId = e.target.value;
                updatedBeacons[newActivationId] = updatedBeacons[activationId];
                delete updatedBeacons[activationId];
                setExperienceData({ ...experienceData, beacons: updatedBeacons });
              }}
              error={!!fieldErrors.beacons}
              helperText={fieldErrors.beacons}
            />
            <TextField
              sx={{ width: "40%", mr: 2 }}
              placeholder="MAC Address"
              value={mac}
              onChange={(e) => {
                setExperienceData({
                  ...experienceData,
                  beacons: { ...experienceData.beacons, [activationId]: e.target.value },
                });
              }}
              error={!!fieldErrors.beacons}
              helperText={fieldErrors.beacons}
            />
            <IconButton
              onClick={() => {
                const updatedBeacons = { ...experienceData.beacons };
                delete updatedBeacons[activationId];
                setExperienceData({ ...experienceData, beacons: updatedBeacons });
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => {
            setExperienceData({
              ...experienceData,
              beacons: { ...experienceData.beacons, "": "" },
            });
          }}
        >
          Add Beacon
        </Button>
        {fieldErrors.beacons && (
          <Box color="red" mt={1}>
            {fieldErrors.beacons}
          </Box>
        )}
      </Box>

      {error && (
        <Box mb={2} p={1}>
          <TextField
            error
            label="Error"
            InputProps={{
              readOnly: true,
              sx: {
                "& input": {
                  color: "red",
                },
              },
            }}
            value={error}
            variant="standard"
          />
        </Box>
      )}
      <Button
        variant="contained"
        color="info"
        type="submit"
        disabled={loading}
      >
        {loading ? <CircularProgress size={30} sx={{ color: green[500] }} /> : "Save"}
      </Button>
    </Box>
  );
};

export default ExperienceForm;